import {
  activeFields,
  getOpeningStatus,
  languageLabel,
  validateSubmission,
  type AnswerMap,
  type AnswerValue,
  type CheckInPayload,
  type FieldError,
  type FieldOption,
  type FlowContext,
  type FormFieldDefinition,
  type KioskConfig,
  type KioskVisitorType,
  type OptionList,
} from '@foyer/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flag } from './components/Flag.js';
import { FormRenderer, InfoBlock } from './components/FormRenderer.js';
import { Banner, Button, CenterState, Icon, iconFor, Spinner, Steps } from './components/ui.js';
import { applyDirection, hasTranslation, localized, strings as stringsFor } from './i18n/index.js';
import * as api from './lib/api.js';
import { Pairing } from './screens/Pairing.js';
import { SetupRequired } from './screens/SetupRequired.js';

/**
 * The kiosk application.
 *
 * One explicit state machine rather than a router: a tablet in a hallway has no
 * URL bar, no back button and no bookmarks, and every transition has to be able
 * to wipe the visitor's data. Modelling that as screens in a reducer makes the
 * "discard everything and go home" path a single, auditable function.
 */

/**
 * Everything the visitor chose before the form. Carried in the screen itself
 * rather than in separate state, so a transition that submits right away (no
 * field to fill in) never reads a choice React has not committed yet.
 */
interface Choice {
  type: KioskVisitorType;
  reason: string | null;
  contact: string | null;
  hasAppointment: boolean;
}

type Screen =
  | { name: 'home' }
  | { name: 'appointment'; type: KioskVisitorType }
  | { name: 'reason'; type: KioskVisitorType }
  | { name: 'contact'; type: KioskVisitorType; reason: string | null }
  | ({ name: 'form' } & Choice)
  | { name: 'sending' }
  // `hints` are the form's QR hints the visitor saw, offered once more.
  | { name: 'success'; ticketCode: string | null; queued: boolean; hints: FormFieldDefinition[] }
  // Deliberately no raw error text: a server message can quote what the visitor
  // typed, and that must not survive on screen or in state. The screen keeps
  // only what is needed to offer a retry.
  | { name: 'error'; retry: Choice | null };

function flowOf(choice: Choice): FlowContext {
  return { reason: choice.reason, hasAppointment: choice.hasAppointment };
}

/** The fields a choice puts on screen; none means there is nothing to ask. */
function fieldsFor(choice: Choice): number {
  const form = choice.type.form;
  if (!form) return 0;
  return activeFields(form, {}, flowOf(choice)).length;
}

/** Hints with a QR code that were on screen for these answers. */
function qrHintsFor(choice: Choice, values: AnswerMap): FormFieldDefinition[] {
  const form = choice.type.form;
  if (!form) return [];
  return activeFields(form, values, flowOf(choice)).filter((f) => f.type === 'info' && Boolean(f.linkUrl));
}

/** A QR code needs time to be found, focused and scanned. */
const QR_SUCCESS_MIN_MS = 30_000;

/** Fresh UUID per submission - the idempotency key the API deduplicates on. */
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Fallback for older WebViews on cheap Android tablets.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function applyBranding(config: KioskConfig): void {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', config.branding.primaryColor);
  root.style.setProperty('--brand-secondary', config.branding.secondaryColor);
  root.style.setProperty('--brand-bg', config.branding.backgroundColor);
  document.title = `${config.tenant.name} - ${config.location.name}`;
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', config.branding.secondaryColor);
}

export function App(): JSX.Element {
  const [config, setConfig] = useState<KioskConfig | null>(api.getCachedConfig());
  const [paired, setPaired] = useState<boolean>(() => api.getDeviceToken() !== null);
  const [setupRequired, setSetupRequired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [staleConfig, setStaleConfig] = useState(false);

  const [screen, setScreen] = useState<Screen>({ name: 'home' });
  const [language, setLanguage] = useState('de');
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [idleWarning, setIdleWarning] = useState(false);

  const t = stringsFor(language);

  /* ------------------------------------------------------------- bootstrap */

  const loadConfig = useCallback(async () => {
    if (!api.getDeviceToken()) {
      // No token yet: before offering the code field, find out whether there is
      // anything to pair with. A failed check is treated as "set up" - the
      // pairing screen handles being offline, this hint does not need to.
      const status = await api.getSetupStatus().catch(() => null);
      setSetupRequired(status?.setupRequired ?? false);
      setPaired(false);
      setLoading(false);
      return;
    }
    try {
      const { config: fresh, fromCache } = await api.fetchConfig();
      setConfig(fresh);
      setStaleConfig(fromCache);
      setPaired(true);
      applyBranding(fresh);
      setLanguage((current) => (fresh.languages.includes(current) ? current : fresh.defaultLanguage));
    } catch (err) {
      if (
        err instanceof api.ApiError &&
        (err.code === 'device_not_paired' || err.code === 'device_revoked')
      ) {
        api.unpair();
        setPaired(false);
        setConfig(null);
      } else {
        setStaleConfig(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  // Arabic is right to left; the document attribute mirrors the whole kiosk.
  useEffect(() => {
    applyDirection(language);
  }, [language]);

  /* ------------------------------------------------- connectivity + outbox */

  const refreshPending = useCallback(async () => {
    setPending(await api.outbox.count());
  }, []);

  useEffect(() => {
    const goOnline = (): void => {
      setOnline(true);
      void api.flushOutbox().then(refreshPending);
    };
    const goOffline = (): void => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [refreshPending]);

  // A tick can outlive its interval on a bad connection (the batch flush alone
  // allows 30s). Without this guard the requests would pile up on top of each
  // other and every one of them would fight over the same outbox rows.
  const tickBusy = useRef(false);
  const lastPruneAt = useRef(0);

  useEffect(() => {
    if (!paired) return undefined;

    // One timer drives heartbeat, outbox flush and config refresh. Everything
    // the tablet needs from the server happens on this tick, which keeps the
    // request pattern predictable for the operator watching the logs.
    const tick = async (): Promise<void> => {
      if (tickBusy.current) return;
      tickBusy.current = true;
      try {
        // Retention is enforced here rather than only at startup: a kiosk
        // tablet runs for months without a reload, and an entry the server
        // never accepted must not sit in IndexedDB past the retention window.
        if (Date.now() - lastPruneAt.current > 3_600_000) {
          lastPruneAt.current = Date.now();
          await api.outbox.prune().catch(() => undefined);
        }
        await api.flushOutbox().catch(() => undefined);
        await refreshPending();
        try {
          const result = await api.heartbeat(await api.outbox.count());
          setOnline(true);
          setStaleConfig(false);
          // The admin changed something - pick it up without a restart.
          if (config && result.configVersion !== config.configVersion) await loadConfig();
        } catch (err) {
          // A registration the server no longer knows is not a network problem.
          // Showing "offline" for it would leave a deleted or unpaired tablet
          // sitting on the visitor screen until somebody reloads it by hand,
          // which is precisely when nobody is standing next to it.
          if (
            err instanceof api.ApiError &&
            (err.code === 'device_not_paired' || err.code === 'device_revoked')
          ) {
            api.unpair();
            setPaired(false);
            setConfig(null);
          } else {
            setOnline(false);
          }
        }
      } finally {
        tickBusy.current = false;
      }
    };

    void tick();
    const interval = window.setInterval(() => void tick(), (config?.heartbeatIntervalSeconds ?? 60) * 1000);
    return () => window.clearInterval(interval);
  }, [paired, config, loadConfig, refreshPending]);

  useEffect(() => {
    // An unpaired tablet has no heartbeat tick, so retention is enforced once
    // on boot instead. Leftovers here can only come from an earlier pairing.
    if (paired) return;
    void api.outbox
      .prune()
      .then(refreshPending)
      .catch(() => undefined);
  }, [paired, refreshPending]);

  /* --------------------------------------------------------- idle handling */

  /**
   * Counts how often the visitor flow was torn down. An in-flight submission
   * captures the value it started with; if a reset happened in the meantime the
   * late response is discarded instead of painting a ticket number - and with
   * it a stranger's confirmation - onto an already cleared start screen.
   */
  const generation = useRef(0);

  // Only the two values `reset` actually reads, so a heartbeat that replaces the
  // whole config object does not give the visitor a fresh idle window.
  const defaultLanguage = config?.defaultLanguage ?? null;
  const idleTimeoutMs = config ? config.idleTimeoutSeconds * 1000 : 0;
  const successScreenMs = config ? config.successScreenSeconds * 1000 : 0;

  const reset = useCallback(
    (keepLanguage = false) => {
      // Everything the visitor typed is dropped here, deliberately and
      // completely: unsubmitted personal data must not survive a timeout.
      generation.current += 1;
      setAnswers({});
      setConsent(false);
      setErrors([]);
      setIdleWarning(false);
      setScreen({ name: 'home' });
      if (!keepLanguage && defaultLanguage) setLanguage(defaultLanguage);
    },
    [defaultLanguage],
  );

  const idleTimer = useRef<number | null>(null);
  const warnTimer = useRef<number | null>(null);

  const restartIdleTimers = useCallback(() => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    if (warnTimer.current) window.clearTimeout(warnTimer.current);
    setIdleWarning(false);

    // Nothing to time out on the start screen, and never while a submission is
    // on the wire: a timeout there would clear the flow the visitor is waiting
    // on and hide the confirmation they are entitled to see.
    if (screen.name === 'home' || screen.name === 'sending' || idleTimeoutMs <= 0) return;

    warnTimer.current = window.setTimeout(
      () => setIdleWarning(true),
      Math.max(idleTimeoutMs - 15_000, idleTimeoutMs * 0.7),
    );
    idleTimer.current = window.setTimeout(() => reset(), idleTimeoutMs);
  }, [idleTimeoutMs, screen.name, reset]);

  useEffect(() => {
    restartIdleTimers();
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
    const handler = (): void => restartIdleTimers();
    for (const e of events) window.addEventListener(e, handler, { passive: true });
    return () => {
      for (const e of events) window.removeEventListener(e, handler);
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      if (warnTimer.current) window.clearTimeout(warnTimer.current);
    };
  }, [restartIdleTimers]);

  useEffect(() => {
    // The screen went dark and came back, or the tablet was rebooted: always
    // return to a clean start screen rather than showing a stranger's form.
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible' && screen.name !== 'home') reset();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [reset, screen.name]);

  useEffect(() => {
    /*
     * The back/forward cache. Going to /admin and pressing "back" freezes this
     * page with its React state intact and thaws it again on return - no
     * reload, no `visibilitychange`, so neither of the guards above sees it.
     * `pageshow` with `persisted` is the only event that fires, and it is the
     * one place where a stranger's half-filled form could otherwise reappear.
     *
     * `pagehide` is the same guard one step earlier - it also covers the plain
     * unload path, where no `pageshow` ever follows. It is not a replacement
     * for the handler above: whether React still commits before the browser
     * freezes the page is not guaranteed, so `pageshow` stays the one that has
     * to be right.
     */
    const onPageHide = (): void => reset();
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) reset();
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [reset]);

  /* ----------------------------------------------------------- derived data */

  const lists = useMemo<Record<string, OptionList>>(() => {
    // The server ships every list a form references. Older cached configs did
    // not carry them, so entries embedded in a field still count - a tablet
    // that starts offline on a stale config keeps whatever it had.
    const out: Record<string, OptionList> = {};
    for (const type of config?.visitorTypes ?? []) {
      for (const field of type.form?.fields ?? []) {
        if (field.listKey && field.options && field.options.length > 0) {
          out[field.listKey] = {
            id: field.listKey,
            tenantId: config?.tenant.id ?? '',
            key: field.listKey,
            name: field.listKey,
            items: field.options,
          };
        }
      }
    }
    return { ...out, ...(config?.lists ?? {}) };
  }, [config]);

  const openingStatus = useMemo(
    () => (config ? getOpeningStatus(config.openingHours, new Date()) : null),
    [config],
  );

  /* ---------------------------------------------------------------- actions */

  const setAnswer = useCallback((name: string, value: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => prev.filter((e) => e.field !== name));
  }, []);

  /**
   * True from the first tap on "send" until the request has settled. A ref, not
   * state: two taps on a sluggish tablet can both run before React re-renders,
   * and each would mint its own idempotency key - which is exactly the case the
   * server-side deduplication cannot catch.
   */
  const submitting = useRef(false);

  const submit = useCallback(
    async (choice: Choice, values: AnswerMap) => {
      if (!config || submitting.current) return;

      submitting.current = true;
      const submittedIn = generation.current;
      const { type, reason, contact, hasAppointment } = choice;

      const payload: CheckInPayload = {
        idempotencyKey: newIdempotencyKey(),
        visitorTypeKey: type.key,
        formVersion: type.form?.version ?? null,
        reason,
        contactTarget: contact,
        // Whom the appointment is with is a form field now (a showWhen on
        // `@appointment`), so it can be a list and drive routing.
        appointment: type.askAppointment
          ? { hasAppointment, withPerson: null, reference: null, company: null }
          : null,
        answers: values,
        language,
        clientCreatedAt: new Date().toISOString(),
        consentAccepted: consent || !config.branding.privacyNotice,
      };

      setScreen({ name: 'sending' });
      try {
        const result = await api.submitCheckIn(payload);
        await refreshPending();
        // The visitor walked away (or the tablet slept) while this was in
        // flight. The check-in is safely stored either way, but the start
        // screen stays a start screen - no ticket number for a stranger.
        if (generation.current !== submittedIn) return;
        setScreen({
          name: 'success',
          ticketCode: result.ticketCode,
          queued: result.outcome === 'queued',
          hints: qrHintsFor(choice, values),
        });
      } catch (err) {
        if (generation.current !== submittedIn) return;
        if (err instanceof api.ApiError && err.details && err.details.length > 0) {
          setErrors(
            err.details.map((d) => ({
              field: d.field,
              code: (d.code ?? 'required') as FieldError['code'],
              message: d.message,
            })),
          );
          setScreen({ name: 'form', ...choice });
          return;
        }
        setScreen({ name: 'error', retry: choice });
      } finally {
        submitting.current = false;
      }
    },
    [config, consent, language, refreshPending],
  );

  /**
   * After the last choice: the form if it has something to ask for this
   * reason, otherwise straight to the waiting number. A guest or "Sonstiges"
   * enters nothing, so there is nothing to show and nothing to consent to.
   */
  const proceed = useCallback(
    (choice: Choice) => {
      setErrors([]);
      if (fieldsFor(choice) > 0) setScreen({ name: 'form', ...choice });
      else void submit(choice, {});
    },
    [submit],
  );

  /* -------------------------------------------------- success auto-dismiss */

  const hasQr = screen.name === 'success' && screen.hints.length > 0;

  useEffect(() => {
    // Keyed on the screen *name*, not the screen object: a config refresh in the
    // background used to replace the object and restart this timer, leaving the
    // confirmation - including the ticket number - up for another full round.
    if (screen.name !== 'success' || successScreenMs <= 0) return undefined;
    const timer = window.setTimeout(
      () => reset(),
      hasQr ? Math.max(successScreenMs, QR_SUCCESS_MIN_MS) : successScreenMs,
    );
    return () => window.clearTimeout(timer);
  }, [screen.name, successScreenMs, hasQr, reset]);

  /* ----------------------------------------------------------------- render */

  if (!paired && setupRequired) return <SetupRequired strings={t} />;

  if (!paired) {
    return (
      <Pairing
        strings={t}
        onPaired={(fresh) => {
          setConfig(fresh);
          applyBranding(fresh);
          setLanguage(fresh.defaultLanguage);
          setPaired(true);
          setScreen({ name: 'home' });
        }}
      />
    );
  }

  if (loading || !config) {
    return (
      <div className="app">
        <div className="main">
          <div className="center-state">
            <Spinner />
            <p className="subline">{t.loading}</p>
          </div>
        </div>
      </div>
    );
  }

  const activeTypes = config.visitorTypes;
  const steps = [
    { key: 'reason', label: t.stepReason },
    { key: 'details', label: t.stepDetails },
    { key: 'done', label: t.stepDone },
  ];
  const stepIndex =
    screen.name === 'home' ||
    screen.name === 'reason' ||
    screen.name === 'contact' ||
    screen.name === 'appointment'
      ? 0
      : screen.name === 'form'
        ? 1
        : 2;

  const goHome = (): void => reset();

  /** Without an appointment: reason, then contact, then whatever the form asks. */
  const afterAppointment = (type: KioskVisitorType): void => {
    if (type.reasons.length > 0) setScreen({ name: 'reason', type });
    else if (type.contactTargets.length > 0) setScreen({ name: 'contact', type, reason: null });
    else proceed({ type, reason: null, contact: null, hasAppointment: false });
  };

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          {config.branding.logoUrl ? (
            <img src={config.branding.logoUrl} alt="" />
          ) : (
            <div className="choice-icon" style={{ width: 44, height: 44, borderRadius: 12 }}>
              <Icon name="home" size={22} />
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div className="brand-name">{config.tenant.name}</div>
            <div className="brand-sub">{config.location.name}</div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {!online || staleConfig ? (
            <span className="pill pill-offline">
              <Icon name="wifi-off" size={14} />
              {t.offline}
            </span>
          ) : null}
          {pending > 0 ? (
            <span className="pill">
              <span className="dot" />
              {pending} {t.pendingCount}
            </span>
          ) : null}

          {config.languages.length > 1 ? (
            <div className="lang-switch" role="group" aria-label="Sprache">
              {config.languages
                .filter((code) => hasTranslation(code))
                .map((code) => (
                  <button
                    key={code}
                    type="button"
                    className="lang-btn"
                    aria-pressed={language === code}
                    lang={code}
                    onClick={() => setLanguage(code)}
                  >
                    <Flag language={code} />
                    <span>{languageLabel(code)}</span>
                  </button>
                ))}
            </div>
          ) : null}
        </div>
      </header>

      <main className="main">
        {idleWarning && screen.name !== 'success' ? (
          <Banner tone="warning" icon="clock">
            {t.idleWarning}
          </Banner>
        ) : null}

        {!online && screen.name === 'home' ? (
          <Banner tone="info" icon="wifi-off">
            {t.offlineHint}
          </Banner>
        ) : null}

        {screen.name !== 'home' && screen.name !== 'success' && screen.name !== 'error' ? (
          <Steps steps={steps} current={stepIndex} />
        ) : null}

        {/* ------------------------------------------------------------ home */}
        {screen.name === 'home' ? (
          <div className="fade-in">
            {openingStatus && !openingStatus.open && config.closedBehaviour !== 'allow' ? (
              <Banner tone="warning" icon="clock">
                {config.closedMessage ?? t.closedHint}
                {config.location.phone ? ` ${t.closedCallUs} ${config.location.phone}.` : ''}
              </Banner>
            ) : null}

            <h1 className="headline">
              {localized(config.branding.welcomeTitle, config.branding.welcomeTitleI18n, language) ||
                t.welcome}
            </h1>
            <p className="subline">
              {localized(config.branding.welcomeSubtitle, config.branding.welcomeSubtitleI18n, language) ||
                t.chooseReason}
            </p>

            {activeTypes.length === 0 ? (
              <Banner tone="danger" icon="alert">
                {t.notConfigured} - {t.notConfiguredHint}
              </Banner>
            ) : (
              <div className="choices">
                {activeTypes.map((type) => (
                  <button
                    key={type.key}
                    type="button"
                    className="choice"
                    onClick={() => {
                      setErrors([]);
                      // The appointment question comes first: whoever has one
                      // is expected and needs no reason.
                      if (type.askAppointment) setScreen({ name: 'appointment', type });
                      else afterAppointment(type);
                    }}
                  >
                    <span className="choice-icon">
                      <Icon name={iconFor(type.icon)} size={30} />
                    </span>
                    <span className="choice-title">{localized(type.name, type.nameI18n, language)}</span>
                    {type.description ? (
                      <span className="choice-desc">
                        {localized(type.description, type.descriptionI18n, language)}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            )}

            {/*
              Staff entrance. A kiosk tablet runs full screen with no address
              bar, so without this there is no way to reach the administration
              on the device itself. Kept small, muted and below the fold of the
              visitor choices so it never reads as a fourth option - but with a
              full 44px touch target, because an adult has to hit it reliably.

              Plain absolute link, no query string: the kiosk is served from /
              and the administration from /admin/ behind the same proxy. Nothing
              about this device travels with it, and the target is the login
              form - access is decided server side, by the session cookie.

              The trailing slash matters. "/admin" is outside the scope of the
              administration's service worker, so a session started there can
              never enable push notifications. The proxy redirects it, but
              linking straight to the correct path saves the round trip.
            */}
            <div className="admin-link-row">
              <a className="admin-link" href="/admin/" rel="nofollow">
                <Icon name="shield" size={15} />
                {t.adminLink}
              </a>
            </div>
          </div>
        ) : null}

        {/* ---------------------------------------------------------- reason */}
        {screen.name === 'reason' ? (
          <div className="fade-in">
            <h2 className="section-title">{t.chooseReason}</h2>
            <p className="subline">{localized(screen.type.name, screen.type.nameI18n, language)}</p>
            <div className="option-list">
              {screen.type.reasons.map((reason: FieldOption) => (
                <button
                  key={reason.value}
                  type="button"
                  className="option-row"
                  onClick={() => {
                    const next = screen.type;
                    if (next.contactTargets.length > 0)
                      setScreen({ name: 'contact', type: next, reason: reason.value });
                    else proceed({ type: next, reason: reason.value, contact: null, hasAppointment: false });
                  }}
                >
                  <span className="option-label">
                    {reason.icon ? (
                      <span className="option-icon">
                        <Icon name={iconFor(reason.icon)} size={24} />
                      </span>
                    ) : null}
                    <span>{localized(reason.label, reason.labelI18n, language)}</span>
                  </span>
                  <span className="chev">
                    <Icon name="chevron" size={20} />
                  </span>
                </button>
              ))}
            </div>
            <div className="actions">
              <Button variant="secondary" icon="back" onClick={goHome}>
                {t.back}
              </Button>
            </div>
          </div>
        ) : null}

        {/* --------------------------------------------------------- contact */}
        {screen.name === 'contact' ? (
          <div className="fade-in">
            <h2 className="section-title">{t.visitingWhom}</h2>
            <p className="subline">{localized(screen.type.name, screen.type.nameI18n, language)}</p>
            <div className="option-list">
              {screen.type.contactTargets.map((target: FieldOption) => (
                <button
                  key={target.value}
                  type="button"
                  className="option-row"
                  onClick={() =>
                    proceed({
                      type: screen.type,
                      reason: screen.reason,
                      contact: target.value,
                      hasAppointment: false,
                    })
                  }
                >
                  <span>{localized(target.label, target.labelI18n, language)}</span>
                  <span className="chev">
                    <Icon name="chevron" size={20} />
                  </span>
                </button>
              ))}
            </div>
            <div className="actions">
              <Button variant="secondary" icon="back" onClick={goHome}>
                {t.back}
              </Button>
            </div>
          </div>
        ) : null}

        {/* ----------------------------------------------------- appointment */}
        {screen.name === 'appointment' ? (
          <div className="fade-in">
            <h2 className="section-title">{t.appointmentQuestion}</h2>
            <div className="option-list" style={{ marginTop: 18 }}>
              <button
                type="button"
                className="option-row"
                onClick={() =>
                  proceed({ type: screen.type, reason: null, contact: null, hasAppointment: true })
                }
              >
                <span>{t.appointmentYes}</span>
                <span className="chev">
                  <Icon name="chevron" size={20} />
                </span>
              </button>
              <button type="button" className="option-row" onClick={() => afterAppointment(screen.type)}>
                <span>{t.appointmentNo}</span>
                <span className="chev">
                  <Icon name="chevron" size={20} />
                </span>
              </button>
            </div>
            <div className="actions">
              <Button variant="secondary" icon="back" onClick={goHome}>
                {t.back}
              </Button>
            </div>
          </div>
        ) : null}

        {/* ------------------------------------------------------------ form */}
        {screen.name === 'form' && screen.type.form ? (
          <form
            className="fade-in"
            onSubmit={(e) => {
              e.preventDefault();
              const choice: Choice = {
                type: screen.type,
                reason: screen.reason,
                contact: screen.contact,
                hasAppointment: screen.hasAppointment,
              };
              const form = screen.type.form;
              if (!form) return;
              const result = validateSubmission(form, answers, { lists, flow: flowOf(choice) });
              const consentMissing = Boolean(config.branding.privacyNotice) && !consent;
              if (!result.ok || consentMissing) {
                setErrors([
                  ...result.errors,
                  ...(consentMissing
                    ? [{ field: '__consent', code: 'required' as const, message: t.privacyRequired }]
                    : []),
                ]);
                return;
              }
              setErrors([]);
              setAnswers(result.values);
              // No review page: the visitor sees the waiting number right away.
              void submit(choice, result.values);
            }}
          >
            <h2 className="section-title">{t.yourDetails}</h2>
            <p className="subline">{localized(screen.type.name, screen.type.nameI18n, language)}</p>

            {errors.some((e) => e.field !== '__consent') ? (
              <Banner tone="danger" icon="alert">
                {t.requiredHint}
              </Banner>
            ) : null}

            <div className="card">
              <FormRenderer
                form={screen.type.form}
                lists={lists}
                values={answers}
                flow={flowOf(screen)}
                errors={errors}
                language={language}
                strings={t}
                onChange={setAnswer}
              />

              {config.branding.privacyNotice ? (
                <div style={{ marginTop: 20 }}>
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => {
                        setConsent(e.target.checked);
                        // Ticking the box answers the complaint - drop it now
                        // rather than leaving a red line under a correct field.
                        if (e.target.checked)
                          setErrors((prev) => prev.filter((x) => x.field !== '__consent'));
                      }}
                    />
                    <span>
                      <strong>{t.privacyTitle}: </strong>
                      {config.branding.privacyNotice}
                      <br />
                      {t.privacyAccept}
                    </span>
                  </label>
                  {errors.some((e) => e.field === '__consent') ? (
                    <div className="error" role="alert">
                      <Icon name="alert" size={16} />
                      {t.privacyRequired}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="actions">
              <Button variant="secondary" icon="back" onClick={goHome}>
                {t.cancel}
              </Button>
              <span className="spacer" />
              <Button type="submit">{t.submit}</Button>
            </div>
          </form>
        ) : null}

        {/* --------------------------------------------------------- sending */}
        {screen.name === 'sending' ? (
          <div className="center-state">
            <Spinner />
            <p className="subline">{t.submitting}...</p>
          </div>
        ) : null}

        {/* --------------------------------------------------------- success */}
        {screen.name === 'success' ? (
          <CenterState
            tone={screen.queued ? 'warning' : 'success'}
            icon={screen.queued ? 'clock' : 'check'}
            title={screen.queued ? t.queuedTitle : t.thankYou}
          >
            {/* The number first: "Ihre Wartenummer lautet 042", then where to wait. */}
            {screen.ticketCode ? (
              <div>
                <div className="summary-label">{t.ticketNumber}</div>
                <div className="ticket">{screen.ticketCode}</div>
              </div>
            ) : null}
            {screen.queued || !screen.ticketCode ? (
              <p className="subline" style={{ margin: 0 }}>
                {screen.queued ? t.queuedHint : t.submitted}
              </p>
            ) : null}
            {!screen.queued ? (
              <p className="subline" style={{ margin: 0 }}>
                {t.waitHint}
              </p>
            ) : null}
            {screen.hints.length > 0 ? (
              <div className="success-hints">
                <p className="subline" style={{ margin: 0 }}>
                  {t.scanFormHint}
                </p>
                {screen.hints.map((field) => (
                  <InfoBlock key={field.name} field={field} language={language} />
                ))}
              </div>
            ) : null}
            <Button variant="secondary" onClick={goHome}>
              {t.startOver}
            </Button>
          </CenterState>
        ) : null}

        {/* ----------------------------------------------------------- error */}
        {screen.name === 'error' ? (
          <CenterState tone="danger" icon="alert" title={t.errorTitle}>
            <p className="subline" style={{ margin: 0 }}>
              {t.errorHint}
            </p>
            <div className="actions" style={{ justifyContent: 'center' }}>
              {/* The answers are still in memory, so the visitor can correct
                  something and send again instead of retyping everything. */}
              {screen.retry ? (
                <Button
                  onClick={() => {
                    // Back to the form, or - when there was nothing to fill
                    // in - straight into a second attempt.
                    if (screen.retry) proceed(screen.retry);
                  }}
                >
                  {t.tryAgain}
                </Button>
              ) : null}
              <Button variant="secondary" onClick={goHome}>
                {t.startOver}
              </Button>
            </div>
          </CenterState>
        ) : null}
      </main>

      <footer className="footer">
        <span>{config.branding.footerText ?? config.location.address ?? ''}</span>
        <span style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {config.branding.privacyUrl ? (
            <a href={config.branding.privacyUrl} target="_blank" rel="noreferrer">
              {t.privacyTitle}
            </a>
          ) : null}
          {config.branding.imprintUrl ? (
            <a href={config.branding.imprintUrl} target="_blank" rel="noreferrer">
              Impressum
            </a>
          ) : null}
          {config.branding.showPoweredBy ? <span>{config.branding.poweredByText}</span> : null}
        </span>
      </footer>
    </div>
  );
}
